import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import PermissionRoute from './components/PermissionRoute';

const Login = React.lazy(() => import('./pages/Login'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Reports = React.lazy(() => import('./pages/Reports'));
const BookingCalendar = React.lazy(() => import('./pages/BookingCalendar'));
const MyBookings = React.lazy(() => import('./pages/MyBookings'));
const PastBookings = React.lazy(() => import('./pages/PastBookings'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AdminAllBookings = React.lazy(() => import('./pages/AdminAllBookings'));
const SwapStationConfig = React.lazy(() => import('./pages/SwapStationConfig'));
const UserAdminDashboard = React.lazy(() => import('./pages/UserAdminDashboard'));
const UserAdminCreateAdminLogin = React.lazy(() => import('./pages/UserAdminCreateAdminLogin'));
const UserAdminUsers = React.lazy(() => import('./pages/UserAdminUsers'));
const UserAdminCreateUser = React.lazy(() => import('./pages/UserAdminCreateUser'));
const UserAdminCreateOperator = React.lazy(() => import('./pages/UserAdminCreateOperator'));
const UserAdminOperators = React.lazy(() => import('./pages/UserAdminOperators'));
const UserAdminUpload = React.lazy(() => import('./pages/UserAdminUpload'));
const UserAdminBatteryData = React.lazy(() => import('./pages/UserAdminBatteryData'));
const UserAdminUserTrucks = React.lazy(() => import('./pages/UserAdminUserTrucks'));
const AdminChargingRecords = React.lazy(() => import('./pages/AdminChargingRecords'));
const MasterListEdit = React.lazy(() => import('./pages/MasterListEdit'));
const DataRecordsDashboard = React.lazy(() => import('./pages/DataRecordsDashboard'));
const UserAdminNotices = React.lazy(() => import('./pages/UserAdminNotices'));
const UserAdminMISConfig = React.lazy(() => import('./pages/UserAdminMISConfig'));
const UserAdminDeletionRequests = React.lazy(() => import('./pages/UserAdminDeletionRequests'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const CommunicationDirectory = React.lazy(() => import('./pages/CommunicationDirectory'));
const SupportTickets = React.lazy(() => import('./pages/SupportTickets'));
const UserAdminSupportTickets = React.lazy(() => import('./pages/UserAdminSupportTickets'));
const FleetMap = React.lazy(() => import('./pages/FleetMap'));
const FleetManagersPage = React.lazy(() => import('./pages/FleetManagersPage'));
const FleetManagerDetail = React.lazy(() => import('./pages/FleetManagerDetail'));

function ProtectedRoute({ children, swapStationManagerOnly = false, adminOnly = false, dataRecordsOnly = false }) {
  const { fleetManager, swapStationManager, admin, dataRecords, loading } = useAuth();

  if (loading) {
    return <div className="text-center">Loading...</div>;
  }

  if (swapStationManagerOnly) {
    return swapStationManager ? children : <Navigate to="/login" />;
  }

  if (adminOnly) {
    return admin ? children : <Navigate to="/login" />;
  }

  if (dataRecordsOnly) {
    return dataRecords ? children : <Navigate to="/login" />;
  }

  // Fleet-manager pages. Other logged-in roles landing here (e.g. clicking the
  // navbar logo to "/") go to THEIR home — not back to the login screen.
  if (fleetManager) return children;
  if (swapStationManager) return <Navigate to="/admin" />;
  if (admin) return <Navigate to="/user-admin" />;
  if (dataRecords) return <Navigate to="/data-records" />;
  return <Navigate to="/login" />;
}

function ReadonlyGuard() {
  const { isReadonly } = useAuth();
  React.useEffect(() => {
    if (isReadonly) {
      document.body.classList.add('readonly-mode');
    } else {
      document.body.classList.remove('readonly-mode');
    }
    return () => document.body.classList.remove('readonly-mode');
  }, [isReadonly]);
  if (!isReadonly) return null;
  return (
    <style>{`
      body.readonly-mode .container form button[type="submit"],
      body.readonly-mode .container form input[type="submit"],
      body.readonly-mode .container button.btn-danger,
      body.readonly-mode .container a.btn-danger,
      body.readonly-mode .container [data-readonly-block] {
        pointer-events: none !important;
        opacity: 0.4 !important;
        cursor: not-allowed !important;
      }
    `}</style>
  );
}

function AppContent() {
  const location = useLocation();
  const hideNavbar = location.pathname === '/login';
  const fullBleed = location.pathname === '/live-status' || location.pathname === '/data-records/map';

  const wrapperClass = hideNavbar ? '' : (fullBleed ? '' : 'container');
  const wrapperStyle = fullBleed ? { padding: '8px 12px' } : undefined;

  return (
    <div className="App">
      <ReadonlyGuard />
      {!hideNavbar && <Navbar />}
      <div className={wrapperClass} style={wrapperStyle}>
        <Suspense fallback={<div className="text-center p-4">Loading...</div>}>
          <Routes>

                <Route path="/login" element={<Login />} />
                <Route path="/admin/login" element={<Navigate to="/login" />} />
                <Route path="/user-admin/login" element={<Navigate to="/login" />} />
                <Route path="/data-records/login" element={<Navigate to="/login" />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />

                <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
                <Route path="/trucks" element={<ProtectedRoute><PermissionRoute permission="fm_trucks"><FleetMap view="list" /></PermissionRoute></ProtectedRoute>} />
                <Route path="/live-status" element={<ProtectedRoute><PermissionRoute permission="fm_live_status"><FleetMap view="map" /></PermissionRoute></ProtectedRoute>} />
                <Route path="/fleet-map" element={<Navigate to="/live-status" replace />} />
                <Route path="/book" element={<ProtectedRoute><BookingCalendar /></ProtectedRoute>} />
                <Route path="/my-bookings" element={<ProtectedRoute><MyBookings /></ProtectedRoute>} />
                <Route path="/my-bookings/past" element={<ProtectedRoute><PastBookings /></ProtectedRoute>} />
                <Route path="/faq" element={<ProtectedRoute><FAQ /></ProtectedRoute>} />
                <Route path="/communication-directory" element={<ProtectedRoute><CommunicationDirectory /></ProtectedRoute>} />
                <Route path="/support-tickets" element={<ProtectedRoute><SupportTickets /></ProtectedRoute>} />
                <Route path="/fleet-managers" element={<ProtectedRoute><FleetManagersPage /></ProtectedRoute>} />
                <Route path="/fleet-managers/:fmId" element={<ProtectedRoute><FleetManagerDetail /></ProtectedRoute>} />

                <Route path="/admin" element={<ProtectedRoute swapStationManagerOnly><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/bookings" element={<ProtectedRoute swapStationManagerOnly><AdminAllBookings /></ProtectedRoute>} />
                <Route path="/admin/charging-records" element={<ProtectedRoute swapStationManagerOnly><AdminChargingRecords /></ProtectedRoute>} />

                <Route path="/user-admin/master-list" element={<ProtectedRoute adminOnly><MasterListEdit /></ProtectedRoute>} />
                <Route path="/user-admin/config" element={<ProtectedRoute adminOnly><SwapStationConfig /></ProtectedRoute>} />
                <Route path="/user-admin" element={<ProtectedRoute adminOnly><UserAdminDashboard /></ProtectedRoute>} />
                <Route path="/user-admin/users" element={<ProtectedRoute adminOnly><UserAdminUsers /></ProtectedRoute>} />
                <Route path="/user-admin/users/new" element={<ProtectedRoute adminOnly><UserAdminCreateUser /></ProtectedRoute>} />
                <Route path="/user-admin/operators/new" element={<ProtectedRoute adminOnly><UserAdminCreateOperator /></ProtectedRoute>} />
                <Route path="/user-admin/operators" element={<ProtectedRoute adminOnly><UserAdminOperators /></ProtectedRoute>} />
                <Route path="/user-admin/upload" element={<ProtectedRoute adminOnly><UserAdminUpload /></ProtectedRoute>} />
                <Route path="/user-admin/battery-data" element={<ProtectedRoute adminOnly><UserAdminBatteryData /></ProtectedRoute>} />
                <Route path="/user-admin/users/:userId/trucks" element={<ProtectedRoute adminOnly><UserAdminUserTrucks /></ProtectedRoute>} />
                <Route path="/user-admin/notices" element={<ProtectedRoute adminOnly><UserAdminNotices /></ProtectedRoute>} />
                <Route path="/user-admin/mis-config" element={<ProtectedRoute adminOnly><UserAdminMISConfig /></ProtectedRoute>} />
                <Route path="/user-admin/requests" element={<ProtectedRoute adminOnly><UserAdminDeletionRequests /></ProtectedRoute>} />
                <Route path="/user-admin/support-tickets" element={<ProtectedRoute adminOnly><UserAdminSupportTickets /></ProtectedRoute>} />
                <Route path="/user-admin/admin-logins" element={<ProtectedRoute adminOnly><UserAdminCreateAdminLogin /></ProtectedRoute>} />

                <Route path="/data-records" element={<ProtectedRoute dataRecordsOnly><DataRecordsDashboard /></ProtectedRoute>} />
                <Route path="/data-records/bookings" element={<ProtectedRoute dataRecordsOnly><AdminDashboard dataRecordsMode /></ProtectedRoute>} />
                <Route path="/data-records/charging-records" element={<ProtectedRoute dataRecordsOnly><AdminChargingRecords dataRecordsMode /></ProtectedRoute>} />
                <Route path="/data-records/vehicles" element={<Navigate to="/data-records/map" replace />} />
                <Route path="/data-records/map" element={<ProtectedRoute dataRecordsOnly><FleetMap readOnly /></ProtectedRoute>} />
              </Routes>
            </Suspense>
          </div>
        </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
